import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Grid, Paper, IconButton, Alert } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';

export default function GuidGenerator() {
  const [guid, setGuid] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  const generateGuid = (): string => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : ((r & 0x3) | 0x8);
      return v.toString(16);
    });
  };

  const handleGenerate = () => {
    setGuid(generateGuid());
    setCopySuccess(false);
  };

  const handleCopy = async () => {
    if (guid) {
      try {
        await navigator.clipboard.writeText(guid);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy: ', err);
      }
    }
  };

  const handleClear = () => {
    setGuid('');
    setCopySuccess(false);
  };
  // Generate initial GUID on component mount
  React.useEffect(() => {
    setGuid(generateGuid());
  }, []);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>GUID Generator</Typography>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Generate globally unique identifiers (GUIDs/UUIDs) for your applications.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Generated GUID"
              value={guid}
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton
                    onClick={handleCopy}
                    disabled={!guid}
                    size="small"
                    sx={{ ml: 1 }}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                )
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={handleGenerate}
                startIcon={<RefreshIcon />}
              >
                Generate New GUID
              </Button>
              <Button
                variant="outlined"
                onClick={handleClear}
              >
                Clear
              </Button>
            </Box>
          </Grid>

          {copySuccess && (
            <Grid item xs={12}>
              <Alert severity="success" sx={{ mt: 1 }}>
                GUID copied to clipboard!
              </Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f9f9f9', mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                About GUIDs
              </Typography>
              <Typography variant="body2" color="text.secondary">
                A GUID (Globally Unique Identifier) is a 128-bit value used to uniquely identify objects or entities. 
                GUIDs are commonly used in software development for database keys, object identifiers, and more.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}
